/**
 * Submission path for the in-app feedback sheet.
 *
 * Extracted from FeedbackSheet so the delivery contract is directly testable —
 * this is where feedback used to be lost:
 *
 *  1. A non-2xx response does not reject `fetch`, so the old code treated a 500
 *     from /api/bug-report as a success and showed the user a checkmark.
 *  2. Only `bug` was ever POSTed; feature ideas and impressions fired a PostHog
 *     event and never reached the issue tracker.
 *
 * Throws on any delivery failure. Callers must surface that to the user rather
 * than swallowing it.
 */

import { trackEvent, getAnalyticsContext } from '../../services/analyticsService';
import { getAuthHeaders } from '../../services/apiClient';

export type FeedbackType = 'bug' | 'feature' | 'impression';

export interface SubmitFeedbackArgs {
  type: FeedbackType;
  /** Raw text from the sheet; trimmed here so callers can't forget to. */
  description: string;
  currentScreen?: string;
}

export async function submitFeedback({
  type,
  description,
  currentScreen,
}: SubmitFeedbackArgs): Promise<void> {
  const trimmed = description.trim();
  const analyticsContext = getAnalyticsContext();

  // Fired first and unconditionally: even if the API call below fails, the
  // feedback still exists as a PostHog event rather than vanishing entirely.
  trackEvent('feedback_submitted', {
    type,
    description: trimmed,
    screen: currentScreen,
    ...(analyticsContext ?? {}),
  });

  const headers = await getAuthHeaders();
  const response = await fetch('/api/bug-report', {
    method: 'POST',
    headers,
    body: JSON.stringify({
      description: trimmed,
      projectName: 'paprika',
      context: {
        source: 'alpha_badge',
        type,
        screen: currentScreen,
        ...analyticsContext,
      },
    }),
  });

  if (!response.ok) {
    throw new Error(`Feedback submission failed: ${response.status}`);
  }

  // `response.ok` alone is not proof of delivery. The Expo web dev server
  // answers unknown paths with the SPA shell — HTTP 200, `text/html` — so a
  // report posted without the API server running looks like a success and is
  // dropped. Only a JSON body acknowledging the write counts.
  let payload: { success?: boolean } | null = null;
  try {
    payload = await response.json();
  } catch {
    throw new Error('Feedback submission returned a non-JSON response (API server not reachable?)');
  }

  if (!payload?.success) {
    throw new Error('Feedback submission was not acknowledged by the server');
  }
}
